import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Activity,
    TrendingUp,
    Zap,
    Clock,
    BarChart3,
    AlertTriangle,
    Rocket,
    RefreshCw,
    Search,
    Share2,
    Megaphone,
    Mail,
    LineChart,
    FileText,
} from 'lucide-react';
import { api } from '../utils/api';

// Module icons mapping
const MODULE_ICONS: Record<string, React.ReactNode> = {
    seo: <Search className="w-4 h-4" />,
    social: <Share2 className="w-4 h-4" />,
    ads: <Megaphone className="w-4 h-4" />,
    email: <Mail className="w-4 h-4" />,
    analytics: <LineChart className="w-4 h-4" />,
    content: <FileText className="w-4 h-4" />,
    other: <Zap className="w-4 h-4" />,
};

interface UsageSummary {
    today: {
        used: number;
        limit: number;
        remaining: number;
        percentUsed: number;
    };
    plan: {
        name: string;
        creditsPerDay: number;
        features: string[];
    };
    history: {
        date: string;
        used: number;
        limit: number;
        byModule: Record<string, number>;
    }[];
    breakdown: {
        module: string;
        credits: number;
        percentage: number;
        icon: string;
    }[];
    recentActivity: {
        timestamp: string;
        credits: number;
        module: string;
        action: string;
        model?: string;
    }[];
}

export default function UsageDashboard() {
    const [refreshKey, setRefreshKey] = useState(0);

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['usage-summary', refreshKey],
        queryFn: async () => {
            const res = await api.get('/usage/summary');
            return res.data.data as UsageSummary;
        },
        refetchInterval: 60000, // Refresh every minute
    });

    const handleRefresh = () => {
        setRefreshKey(k => k + 1);
        refetch();
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-primary-400" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="card p-6 text-center">
                <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-dark-400">Failed to load usage data</p>
                <button
                    onClick={handleRefresh}
                    className="btn btn-primary mt-4"
                >
                    Try Again
                </button>
            </div>
        );
    }

    const { today, plan, history, breakdown, recentActivity } = data;

    // Calculate if user should see upgrade prompt
    const showUpgradePrompt = today.percentUsed >= 75 && plan.name === 'Free Trial';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Usage Dashboard</h1>
                    <p className="text-dark-400 text-sm">
                        Monitor your Sundarban AI credit consumption
                    </p>
                </div>
                <button
                    onClick={handleRefresh}
                    className="btn btn-secondary flex items-center gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Low Credits Warning */}
            {showUpgradePrompt && (
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/50 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-yellow-400" />
                        <div>
                            <p className="font-semibold text-yellow-200">
                                Running low on credits!
                            </p>
                            <p className="text-sm text-yellow-300/80">
                                You've used {today.percentUsed}% of your daily limit. Upgrade to Launch Pad for 40x more credits!
                            </p>
                        </div>
                    </div>
                    <a
                        href="/settings"
                        className="btn bg-yellow-500 hover:bg-yellow-600 text-black flex items-center gap-2"
                    >
                        <Rocket className="w-4 h-4" />
                        Upgrade - $9/mo
                    </a>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Today's Usage */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-dark-400 text-sm">Today's Usage</span>
                        <Activity className="w-5 h-5 text-primary-400" />
                    </div>
                    <div className="text-3xl font-bold mb-2">
                        {today.used.toLocaleString()}
                        <span className="text-lg text-dark-400 font-normal">
                            /{today.limit === -1 ? '∞' : today.limit.toLocaleString()}
                        </span>
                    </div>
                    <div className="w-full bg-dark-700 rounded-full h-2 mb-2">
                        <div
                            className={`h-2 rounded-full transition-all ${today.percentUsed >= 90
                                ? 'bg-red-500'
                                : today.percentUsed >= 75
                                    ? 'bg-yellow-500'
                                    : 'bg-primary-500'
                                }`}
                            style={{ width: `${Math.min(today.percentUsed, 100)}%` }}
                        />
                    </div>
                    <p className="text-sm text-dark-400">
                        {today.remaining === Infinity ? 'Unlimited' : `${today.remaining.toLocaleString()} remaining`}
                    </p>
                </div>

                {/* Current Plan */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-dark-400 text-sm">Current Plan</span>
                        <Zap className="w-5 h-5 text-primary-400" />
                    </div>
                    <div className="text-2xl font-bold mb-2">{plan.name}</div>
                    <p className="text-sm text-dark-400">
                        {plan.creditsPerDay === -1 ? 'Unlimited' : `${plan.creditsPerDay.toLocaleString()}`} credits/day
                    </p>
                </div>

                {/* 7-Day Average */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-dark-400 text-sm">7-Day Average</span>
                        <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="text-3xl font-bold mb-2">
                        {Math.round(history.reduce((a, h) => a + h.used, 0) / history.length).toLocaleString()}
                    </div>
                    <p className="text-sm text-dark-400">credits/day</p>
                </div>

                {/* Recent Activity Count */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-dark-400 text-sm">Recent Actions</span>
                        <Clock className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold mb-2">{recentActivity.length}</div>
                    <p className="text-sm text-dark-400">in last 24 hours</p>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Usage History Chart */}
                <div className="lg:col-span-2 card p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary-400" />
                        Usage History (Last 7 Days)
                    </h2>
                    <div className="space-y-3">
                        {history.slice().reverse().map((day) => (
                            <div key={day.date} className="flex items-center gap-4">
                                <span className="w-20 text-sm text-dark-400">
                                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                </span>
                                <div className="flex-1">
                                    <div className="w-full bg-dark-700 rounded-full h-4">
                                        <div
                                            className="h-4 rounded-full bg-gradient-to-r from-primary-500 to-purple-500 transition-all"
                                            style={{
                                                width: `${day.limit > 0 ? Math.min((day.used / day.limit) * 100, 100) : 0}%`
                                            }}
                                        />
                                    </div>
                                </div>
                                <span className="w-24 text-right text-sm">
                                    {day.used.toLocaleString()} / {day.limit === -1 ? '∞' : day.limit.toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Module Breakdown */}
                <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4">Usage by Module</h2>
                    {breakdown.length > 0 ? (
                        <div className="space-y-4">
                            {breakdown.map((item) => (
                                <div key={item.module} className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-dark-700 flex items-center justify-center">
                                        {MODULE_ICONS[item.module] || <Zap className="w-4 h-4" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium capitalize">
                                                {item.module}
                                            </span>
                                            <span className="text-sm text-dark-400">
                                                {item.credits.toLocaleString()} ({item.percentage}%)
                                            </span>
                                        </div>
                                        <div className="w-full bg-dark-700 rounded-full h-2">
                                            <div
                                                className="h-2 rounded-full bg-primary-500"
                                                style={{ width: `${item.percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-dark-400">
                            <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No usage data yet today</p>
                            <p className="text-sm">Start using AI features to see breakdown</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Activity */}
            <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary-400" />
                    Recent Activity
                </h2>
                {recentActivity.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                                    <th className="pb-3 pr-4">Time</th>
                                    <th className="pb-3 pr-4">Module</th>
                                    <th className="pb-3 pr-4">Action</th>
                                    <th className="pb-3 pr-4">Model</th>
                                    <th className="pb-3 text-right">Credits</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentActivity.slice(0, 10).map((activity, i) => (
                                    <tr key={i} className="border-b border-dark-800">
                                        <td className="py-3 pr-4 text-sm text-dark-400">
                                            {new Date(activity.timestamp).toLocaleTimeString()}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className="inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-dark-700 text-sm capitalize">
                                                {MODULE_ICONS[activity.module]}
                                                {activity.module}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-4 text-sm">{activity.action}</td>
                                        <td className="py-3 pr-4 text-sm text-dark-400">
                                            {activity.model || '-'}
                                        </td>
                                        <td className="py-3 text-right text-sm font-medium">
                                            {activity.credits}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-8 text-dark-400">
                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No recent activity</p>
                        <p className="text-sm">Your AI usage will appear here</p>
                    </div>
                )}
            </div>

            {/* Plan Features */}
            <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4">Your Plan Features</h2>
                <div className="flex flex-wrap gap-2">
                    {plan.features.map((feature, i) => (
                        <span
                            key={i}
                            className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-sm"
                        >
                            {feature}
                        </span>
                    ))}
                </div>
                {plan.name === 'Free Trial' && (
                    <div className="mt-4 pt-4 border-t border-dark-700">
                        <p className="text-sm text-dark-400 mb-3">
                            Upgrade to get more credits and features
                        </p>
                        <a href="/pricing" className="btn btn-primary">
                            View Plans
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
